import { Box, Stack } from "@mui/material";
import { CurrentMerchantFacilities } from "Components/UserAccount/Merchant/CurrentMerchantFacilities";
import { CurrentMerchantAppProducts } from "Components/UserAccount/Merchant/CurrentMerchantAppProducts";
import {
  QueryParamTabs,
  type TabItem,
} from "Components/Shared/Tabs/QueryParamTabs";
import { getPublicInformationTab } from "Components/UserAccount/Shared/PublicInformationTab";
import type { AccountDetailProps as Props } from "Components/UserAccount/types";
import { ContactPersons } from "Components/Shared/Merchant/ContactPersons/ContactPersons";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { PartyType } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { DeactivateAccountButton } from "Components/UserAccount/Shared/DeactivateAccount/DeactivateAccountButton";
import { MerchantPartyDetail } from "Components/UserAccount/Merchant/MerchantPartyDetail/MerchantPartyDetail";
import { RelatedParties } from "Components/Users/UserDetailPageContent/Components/RelatedPartyForm/RelatedParties";
import { UserSettingsSection } from "Components/UserAccount/Shared/UserSettings/UserSettingsSection";
import { VerificationForm } from "Components/UserAccount/Shared/VerificationForm/VerificationForm";
import { logError } from "ErrorService";
import { ErrorText } from "Components/Shared/Errors/ErrorText";

const COMPONENT_RESOURCES = Resources.Components.Account;

export const MerchantUserAccountDetail = ({
  verification,
  canDeactivateAccount,
  party,
}: Props) => {
  const { t } = useResource();

  if (!party.publicID) {
    logError(
      new Error("Merchant party public ID is missing"),
      { party },
      false,
    );
    return <ErrorText>{t(Resources.Errors.MissingId)}</ErrorText>;
  }

  const isLegalEntity = party.type === PartyType.LegalEntity;
  const tabs: Array<TabItem> = [
    getPublicInformationTab(t, party),
    {
      id: "account",
      label: t(COMPONENT_RESOURCES.AccountTab),
      content: (
        <Stack gap={2}>
          <PrimaryPaper>
            <UserSettingsSection />
          </PrimaryPaper>
          {canDeactivateAccount && (
            <PrimaryPaper>
              <DeactivateAccountButton />
            </PrimaryPaper>
          )}
        </Stack>
      ),
    },
    {
      id: "party",
      label: t(COMPONENT_RESOURCES.PersonalInformation),
      content: (
        <PrimaryPaper>
          <MerchantPartyDetail party={party} />
        </PrimaryPaper>
      ),
    },
    {
      id: "products",
      label: t(COMPONENT_RESOURCES.Products),
      content: (
        <PrimaryPaper>
          <CurrentMerchantAppProducts />
        </PrimaryPaper>
      ),
    },

    {
      id: "company",
      label: t(COMPONENT_RESOURCES.Company),
      content: (
        <PrimaryPaper>
          <CurrentMerchantFacilities />
          <ContactPersons publicPartyID={party.publicID} />
          {isLegalEntity && (
            <Box mb={4}>
              <RelatedParties
                relatedParties={party.relatedParties}
                owningPartyID={party.publicID}
              />
            </Box>
          )}
        </PrimaryPaper>
      ),
    },
    {
      id: "verification",
      label: t(COMPONENT_RESOURCES.Verification),
      content: (
        <PrimaryPaper>
          <VerificationForm
            verification={verification}
            partyType={party.type}
          />
        </PrimaryPaper>
      ),
    },
  ];

  return <QueryParamTabs tabs={tabs} />;
};
